import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ReactModal from "react-modal";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import NumberFormat from "react-number-format";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";
import { formatProspectOption } from "./helpers";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const TRANSACTION_TYPE_OPTIONS = [
  { value: "Credit Balance", label: "Credit Balance" },
  { value: "Debit Balance", label: "Debit Balance" },
];

const CATEGORY_OPTIONS_BY_TYPE = {
  "Debit Balance": [
    { value: "Flight Charges", label: "Flight Charges" },
    { value: "Catering", label: "Catering" },
    { value: "Ground Transportation", label: "Ground Transportation" },
    { value: "Other Incidentals", label: "Other Incidentals" },
    { value: "Membership Canceled", label: "Membership Canceled" },
    { value: "Returned Funds on File", label: "Returned Funds on File" },
  ],
  "Credit Balance": [
    { value: "Account Activation", label: "Account Activation" },
    { value: "Refund - Partial Trip", label: "Refund - Partial Trip" },
    { value: "Refund - Canceled Trip", label: "Refund - Canceled Trip" },
    { value: "Refund - Overpayment", label: "Refund - Overpayment" },
    { value: "Funds on File (General)", label: "Funds on File (General)" },
    { value: "Membership Credits", label: "Membership Credits" },
  ],
};

const SOURCE_OPTIONS = [
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Credit Card", label: "Credit Card" },
  { value: "Funds on File (General)", label: "Funds on File (General)" },
  { value: "Funds on File (Membership)", label: "Funds on File (Membership)" },
];

const DEFAULT_OPTIONS_MAX_LIMIT = 50;

const rowAmount = (row) => row?.amount;

const buildInitialState = (row) => {
  const transactionType = rowAmount(row) >= 0 ? "Credit Balance" : "Debit Balance";
  const notes = [
    `Contact Name: ${row?.contactName || ""}`,
    `Reference: ${row?.reference || ""}`,
  ].join("; ");

  return {
    isSaving: false,
    clientId: null,
    clientOption: null,
    onBehalfOf: "",
    transactionType,
    transactionTypeOption: TRANSACTION_TYPE_OPTIONS.find((o) => o.value === transactionType) || null,
    category: null,
    categoryOption: null,
    source: null,
    sourceOption: null,
    notes,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "reset":
      return buildInitialState(action.row);
    default:
      return state;
  }
};

const getProspects = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/prospects/search", { params });
  return data?.prospects ?? [];
};

const getProspectOptions = (prospects) =>
  prospects.flatMap(({ _id, firstAndLastName, lastName, firstName, emails, companyName, assigneeId, assigneeName }) => {
    if (!firstAndLastName && !lastName && !firstName) return [];
    return [formatProspectOption({
      _id,
      firstAndLastName,
      lastName,
      firstName,
      emails,
      companyName,
      assigneeId,
      assigneeName,
    })];
  });

const PaymentLinkLedgerModal = ({ isOpen, row, onRequestClose }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, buildInitialState(row));

  // Re-derive state from the current row each time the modal opens. The modal
  // can reopen for a different transaction without unmounting, so the
  // once-on-mount initial state would otherwise keep the previous row's values.
  useEffect(() => {
    if (isOpen) {
      dispatch({ type: "reset", row });
    }
  }, [isOpen, row?._id]);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently().catch(() => loginWithRedirect());
  }, [getAccessTokenSilently, loginWithRedirect]);

  const categoryOptions = useMemo(
    () => (state.transactionType ? CATEGORY_OPTIONS_BY_TYPE[state.transactionType] || [] : []),
    [state.transactionType],
  );

  const isNotesRequired = state.transactionType === "Debit Balance" && state.category === "Other Incidentals";

  const handleClose = useCallback(() => {
    dispatch({ type: "reset", row });
    onRequestClose(false);
  }, [onRequestClose, row]);

  const handleSubmit = useCallback(async () => {
    if (!state.clientId || !state.category) {
      return toast.error("Please fill in all required fields.");
    }

    if (isNotesRequired && !state.notes?.trim()) {
      return toast.error("Notes is required for Other Incidentals.");
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    const token = await getToken();

    const provider = row?.objectType ? "stripe" : "xero";

    const response = await ai.auth(token)
      .post(`/api/${provider}/transactions/${row._id}/link-ledger`, {
        clientId: state.clientId,
        onBehalfOf: state.onBehalfOf || "",
        transactionType: state.transactionType,
        amount: Math.abs(rowAmount(row) || 0),
        category: state.category,
        source: state.source || null,
        notes: state.notes || "",
      })
      .catch((error) => {
        Sentry.captureException(error);
        toast.error(getApiErrorMessage(error, "Error linking transaction."));
      });

    if (response?.data) {
      toast.success("Transaction linked to Funds Ledger successfully.");
      dispatch({ type: "reset", row });
      await onRequestClose();
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: false });
  }, [state, row, getToken, isNotesRequired, onRequestClose]);

  const loadProspects = useCallback(async (inputValue) => {
    const token = await getToken();
    const prospects = await getProspects(token, { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT });
    return getProspectOptions(prospects);
  }, [getToken]);

  const debouncedLoadProspects = useDebouncedCallback(
    (inputValue, callback) => {
      loadProspects(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const amountPrefix = state.transactionType === "Debit Balance" ? "-$" : "$";

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleClose}
      style={{
        overlay: { zIndex: 999999, backgroundColor: "rgb(32 53 68 / 50%)" },
        content: {
          width: "auto",
          border: "none",
          maxWidth: "700px",
          maxHeight: "85vh",
          minWidth: "calc(min(700px, 95vw))",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header">
        <h4>Link Payment to the Ledger</h4>
      </div>

      <div className="modal-body width-100 modal-body-with-scroll">
        <form className="w-100 base-form mt-10">
          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-grow-1">
              <label className="base-label">
                Client <span className="required">*</span>
              </label>
              <AsyncSelect
                classNamePrefix="select"
                loadOptions={debouncedLoadProspects}
                placeholder="Search Client"
                onChange={(option) => {
                  dispatch({ type: "fieldChanged", field: "clientId", value: option?.value || null });
                  dispatch({ type: "fieldChanged", field: "clientOption", value: option });
                }}
                value={state.clientOption}
                noOptionsMessage={() => "Type to search..."}
                isClearable
                menuPosition="fixed"
              />
            </div>
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-grow-1">
              <label className="base-label">On Behalf Of</label>
              <input
                className="base-input"
                type="text"
                placeholder="Enter On Behalf Of"
                value={state.onBehalfOf}
                onChange={(e) => dispatch({ type: "fieldChanged", field: "onBehalfOf", value: e.target.value })}
              />
            </div>
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-half">
              <label className="base-label">
                Transaction Type <span className="required">*</span>
              </label>
              <Select
                classNamePrefix="select"
                options={TRANSACTION_TYPE_OPTIONS}
                value={state.transactionTypeOption}
                isDisabled
                menuPosition="fixed"
              />
            </div>

            <div className="form-group flex-half">
              <label className="base-label">
                Amount <span className="required">*</span>
              </label>
              <NumberFormat
                className="base-input"
                thousandSeparator
                prefix={amountPrefix}
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                value={Math.abs(rowAmount(row) || 0)}
                disabled
              />
            </div>
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-half">
              <label className="base-label">
                Payment Category <span className="required">*</span>
              </label>
              <Select
                classNamePrefix="select"
                options={categoryOptions}
                placeholder="Select Category"
                onChange={(option) => {
                  dispatch({ type: "fieldChanged", field: "category", value: option?.value || null });
                  dispatch({ type: "fieldChanged", field: "categoryOption", value: option });
                }}
                value={state.categoryOption}
                menuPosition="fixed"
              />
            </div>

            <div className="form-group flex-half">
              <label className="base-label">Source</label>
              <Select
                classNamePrefix="select"
                options={SOURCE_OPTIONS}
                placeholder="Select Source"
                onChange={(option) => {
                  dispatch({ type: "fieldChanged", field: "source", value: option?.value || null });
                  dispatch({ type: "fieldChanged", field: "sourceOption", value: option });
                }}
                value={state.sourceOption}
                isClearable
                menuPosition="fixed"
              />
            </div>
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-grow-1">
              <label className="base-label">
                Notes{isNotesRequired && <span className="required"> *</span>}
              </label>
              <textarea
                className="base-input"
                placeholder="Enter notes"
                rows={3}
                value={state.notes}
                onChange={(e) => dispatch({ type: "fieldChanged", field: "notes", value: e.target.value })}
              />
            </div>
          </div>
        </form>
      </div>

      <div className="modal-footer w-100 mt-10 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          type="button"
          onClick={handleSubmit}
          isLoading={state.isSaving}
        >
          Submit
        </LoadingButton>
        <button
          className="base-button btn-danger flex-half"
          type="button"
          onClick={handleClose}
          disabled={state.isSaving}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default PaymentLinkLedgerModal;
